'use client';

import { useCallback, useState, useEffect } from 'react';

const PANEL_COLLAPSED_KEY = 'player-selection-panel-collapsed';

export function usePlayerSelection() {
  // Initialize with default values (same on server and client to avoid hydration mismatch)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Load from localStorage after hydration (client-only)
  useEffect(() => {
    setIsHydrated(true);

    const stored = localStorage.getItem(PANEL_COLLAPSED_KEY);
    if (stored !== null) {
      setIsPanelCollapsed(stored === 'true');
    }
  }, []);

  // Toggle a player's selection
  const togglePlayer = useCallback((playerId: string) => {
    setSelectedIds(prev => {
      const newIds = new Set(prev);
      
      if (newIds.has(playerId)) {
        newIds.delete(playerId);
      } else {
        newIds.add(playerId);
      }
      
      return newIds;
    });
  }, []);

  // Select multiple players at once
  const selectPlayers = useCallback((playerIds: string[]) => {
    setSelectedIds(prev => {
      const newIds = new Set(prev);
      
      for (const id of playerIds) {
        newIds.add(id);
      }
      
      return newIds;
    });
  }, []);

  // Select all provided players
  const selectAll = useCallback((playerIds: string[]) => {
    setSelectedIds(new Set(playerIds));
  }, []);

  // Clear all selections
  const clearAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Check if a player is selected
  const isSelected = useCallback((playerId: string) => {
    return selectedIds.has(playerId);
  }, [selectedIds]);

  // Toggle panel collapse state
  const togglePanel = useCallback(() => {
    setIsPanelCollapsed(prev => {
      const newState = !prev;
      localStorage.setItem(PANEL_COLLAPSED_KEY, String(newState));
      return newState;
    });
  }, []);

  // Set panel collapse state explicitly
  const setPanelCollapsed = useCallback((collapsed: boolean) => {
    setIsPanelCollapsed(collapsed);
    localStorage.setItem(PANEL_COLLAPSED_KEY, String(collapsed));
  }, []);

  // Sync panel collapse state from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === PANEL_COLLAPSED_KEY && e.newValue !== null) {
        setIsPanelCollapsed(e.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    togglePlayer,
    selectPlayers,
    selectAll,
    clearAll,
    isSelected,
    isPanelCollapsed,
    togglePanel,
    setPanelCollapsed,
    isHydrated, // Expose hydration state for conditional rendering if needed
  };
}